/* eslint-disable eqeqeq */
import { Controller } from "react-hook-form";
import TextInput from "../inputs/TextInput";
import ComboboxInput from "../inputs/ComboboxInput";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Device,
  STATUS_IDS,
  SYSTEM_DETAIL_CODES,
  UserAppointment,
} from "../../constants/common";
import { useTranslation } from "react-i18next";
import { useGetAllSystemDetailCodesDropdown } from "../../hooks/common/query";
import { mapToOptions } from "../../lib/functions";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { Button } from "../button";
import { v4 as uuidv4 } from "uuid";
import toast from "react-hot-toast";
import i18n from "../../i18n";

interface AddDeviceDetailsCardProps {
  clearErrors: any;
  userAppointmentDetails?: UserAppointment;
  setAddedDevices: Dispatch<any>;
  setIsAddVisitorFormVisible: Dispatch<SetStateAction<boolean>>;
  selectedDevice: Device | null;
  setSelectedDevice: React.Dispatch<React.SetStateAction<Device | null>>;
  isDeviceDeleted: boolean;
  setIsDeviceDeleted: Dispatch<SetStateAction<boolean>>;
}

const AddDeviceDetailsCardSchema = Yup.object({
  deviceType: Yup.string(),
  deviceTypeOther: Yup.string(),
  deviceModel: Yup.string(),
  serialNumber: Yup.string().matches(
    /^[a-zA-Z0-9 ]{1,25}$/,
    i18n.language === "en"
      ? "Enter a valid serial number."
      : "أدخل رقمًا تسلسليًا صالحًا."
  ),
  devicePurpose: Yup.string().min(
    12,
    i18n.language === "en"
      ? "Must be at least 12 characters."
      : "يجب أن يكون 12 حرفًا على الأقل."
  ),
  devicePurposeOther: Yup.string(),
});

const AddDeviceDetailsCard = ({
  clearErrors,
  setAddedDevices,
  setIsAddVisitorFormVisible,
  selectedDevice,
  setSelectedDevice,
  isDeviceDeleted,
  setIsDeviceDeleted,
}: AddDeviceDetailsCardProps) => {
  const [selectedType, setSelectedType] = useState<string | number>("");
  const [selectedPurpose, setSelectedPurpose] = useState<string | number>("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useFormWithSchema(AddDeviceDetailsCardSchema);

  const { t } = useTranslation();

  const { data: deviceTypes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.VISITOR_DEVICE_TYPES,
  });

  const { data: devicePurposes } = useGetAllSystemDetailCodesDropdown({
    encryptedId: SYSTEM_DETAIL_CODES.VISITOR_DEVICE_PURPOSES,
  });

  const allDeviceTypes = useMemo(
    () => mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", deviceTypes),
    [deviceTypes]
  );

  const allDevicePurposes = useMemo(
    () =>
      mapToOptions("s_Desc_E", "s_Desc_A", "n_DetailedCode", devicePurposes),
    [devicePurposes]
  );

  useEffect(() => {
    if (selectedDevice) {
      reset({
        deviceType: selectedDevice.deviceType?.toString(),
        deviceTypeOther: selectedDevice.deviceTypeOther,
        deviceModel: selectedDevice.deviceModel,
        serialNumber: selectedDevice.serialNumber,
        // devicePurpose: selectedDevice.devicePurpose?.toString(),
        devicePurpose: selectedDevice.purpose?.toString(),
        devicePurposeOther: selectedDevice.devicePurposeOther,
      });

      setSelectedType(selectedDevice.deviceType);
      setSelectedPurpose(selectedDevice.devicePurpose);
    }
  }, [reset, selectedDevice, setIsDeviceDeleted]);

  useEffect(() => {
    if (isDeviceDeleted) {
      reset({
        deviceType: "",
        deviceTypeOther: "",
        deviceModel: "",
        serialNumber: "",
        devicePurpose: "",
        devicePurposeOther: "",
      });

      setSelectedType("");
      setSelectedPurpose("");

      setSelectedDevice(null);
      setIsAddVisitorFormVisible(false);

      setIsDeviceDeleted(false);
    }
  }, [
    isDeviceDeleted,
    reset,
    selectedDevice,
    setIsAddVisitorFormVisible,
    setIsDeviceDeleted,
    setSelectedDevice,
  ]);

  const onSubmit = useCallback(
    (data: any) => {
      // Check if at least one field is filled
      const hasAnyValue =
        data.deviceType ||
        data.deviceModel ||
        data.serialNumber ||
        data.devicePurpose;

      // Check if all required fields are filled
      const isComplete =
        data.deviceType &&
        data.deviceModel &&
        data.serialNumber &&
        data.devicePurpose &&
        (Number(data.deviceType) !== 2250 || data.deviceTypeOther) &&
        (Number(data.devicePurpose) !== 2254 || data.devicePurposeOther);

      if (hasAnyValue && !isComplete) {
        toast.error(t("fillDeviceDetails"));
        return;
      }

      if (isComplete) {
        const id = uuidv4();

        if (
          data.deviceType ||
          data.deviceModel ||
          data.serialNumber ||
          data.devicePurpose
        ) {
          const details = {
            id,
            deviceType: Number(data.deviceType),
            deviceTypeOther:
              Number(data.deviceType) === 2250 ? data.deviceTypeOther : "",
            deviceModel: data.deviceModel,
            serialNumber: data.serialNumber,
            // devicePurpose: Number(data.devicePurpose),
            purpose: data.devicePurpose,
            devicePurposeOther:
              Number(data.devicePurpose) === 2254
                ? data.devicePurposeOther
                : "",
          };

          if (!selectedDevice) {
            setAddedDevices((prev: any) => [...(prev || []), details]);
          } else {
            setAddedDevices((prev: any[]) =>
              prev.map((device) =>
                (selectedDevice.appointmentDeviceId &&
                  device.appointmentDeviceId ===
                    selectedDevice.appointmentDeviceId) ||
                (selectedDevice.id && device.id === selectedDevice.id)
                  ? {
                      ...device,
                      deviceType: Number(data.deviceType),
                      deviceTypeOther:
                        Number(data.deviceType) === 2250
                          ? data.deviceTypeOther
                          : "",
                      deviceModel: data.deviceModel,
                      serialNumber: data.serialNumber,
                      // devicePurpose: Number(data.devicePurpose),
                      purpose: data.devicePurpose,
                      devicePurposeOther:
                        Number(data.devicePurpose) === 2254
                          ? data.devicePurposeOther
                          : "",
                      approvalStatus: STATUS_IDS.PENDING,
                    }
                  : device
              )
            );
          }

          reset();
          setSelectedDevice(null);
          setIsAddVisitorFormVisible(false);
        }
      }
    },
    [
      reset,
      setAddedDevices,
      selectedDevice,
      setSelectedDevice,
      setIsAddVisitorFormVisible,
      t,
    ]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-4">
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2 lg:gap-4">
        <Controller
          name="deviceType"
          control={control}
          render={({ field: { onChange, name, value } }) => {
            return (
              <ComboboxInput
                options={allDeviceTypes}
                value={
                  allDeviceTypes?.find((item) => item.value == value)
                    ?.labelEn || ""
                }
                onChange={(value) => {
                  onChange(value);
                  setSelectedType(value);
                  clearErrors(name);
                }}
                error={errors?.[name]?.message}
                label={t("deviceType")}
                isInfoToolTip
                infoToolTip={<p>{t("selectDeviceType")}</p>}
              />
            );
          }}
        />

        {selectedType === 2250 && (
          <Controller
            name="deviceTypeOther"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <div className="flex flex-col">
                <TextInput
                  label={t("deviceTypeOther")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                />
              </div>
            )}
          />
        )}

        <Controller
          name="deviceModel"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <div className="flex flex-col">
              <TextInput
                label={t("deviceModel")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
                isInfoToolTip
                infoToolTip={<p>{t("enterDeviceModel")}</p>}
              />
            </div>
          )}
        />

        <Controller
          name="serialNumber"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            <div className="flex flex-col">
              <TextInput
                label={t("serialNumber")}
                value={value}
                onChange={onChange}
                error={errors?.[name]?.message}
              />
            </div>
          )}
        />

        <Controller
          name="devicePurpose"
          control={control}
          render={({ field: { onChange, name, value } }) => (
            // <ComboboxInput
            //   options={allDevicePurposes}
            //   value={
            //     allDevicePurposes?.find((item) => item.value == value)
            //       ?.labelEn || ""
            //   }
            //   onChange={(value) => {
            //     onChange(value);
            //     setSelectedPurpose(value);
            //     clearErrors(name);
            //   }}
            //   error={errors?.[name]?.message}
            //   label={t("devicePurpose")}
            // />
            <TextInput
              label={t("devicePurpose")}
              value={value}
              onChange={onChange}
              error={errors?.[name]?.message}
              min={12}
            />
          )}
        />

        {selectedPurpose === 2254 && (
          <Controller
            name="devicePurposeOther"
            control={control}
            render={({ field: { onChange, name, value } }) => (
              <div className="flex flex-col">
                <TextInput
                  label={t("devicePurposeOther")}
                  value={value}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                />
              </div>
            )}
          />
        )}
      </div>
      <div className="flex items-center justify-between">
        <div />
        <div className="flex items-center gap-2">
          <Button type="button" onClick={handleSubmit(onSubmit)}>
            {!selectedDevice ? t("add") : t("update")}
          </Button>
          <Button
            onClick={() => {
              setIsAddVisitorFormVisible(false);
              setSelectedDevice(null);
            }}
            className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-purple-primary/10"
          >
            {t("cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AddDeviceDetailsCard);
